import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, verticalListSortingStrategy, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import * as React from 'react';
import isEqual from 'react-fast-compare';
import { useDndContext } from '../DndContextV2.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var Context = /*#__PURE__*/React.createContext(null);
function DndDroppableV2(props) {
  var children = props.children,
    connectId = props.connectId,
    direction = props.direction,
    droppableId = props.droppableId,
    items = props.items,
    _props$mode = props.mode,
    mode = _props$mode === void 0 ? "move" : _props$mode;
  var _React$useState = React.useState(items),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    originalItems = _React$useState2[0],
    setOriginalItems = _React$useState2[1];
  var _React$useState3 = React.useState(items),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    itemsState = _React$useState4[0],
    setItemsState = _React$useState4[1];
  var _useDroppable = useDroppable({
      id: droppableId
    }),
    isOver = _useDroppable.isOver,
    setNodeRef = _useDroppable.setNodeRef;
  var dndContext = useDndContext();
  var prevItems = React.useRef(null);
  var prevDndContextItems = React.useRef(dndContext.itemsPerDroppableId[droppableId]);
  React.useEffect(function () {
    dndContext.setDroppableInfoPerDroppableIdRef(droppableId, {
      connectId: connectId,
      direction: direction,
      mode: mode
    });
  }, [connectId, direction, dndContext, droppableId, mode]);
  React.useEffect(function () {
    var newItems;
    if (!isEqual(prevItems.current, items)) {
      newItems = items || [];
      prevItems.current = _toConsumableArray(newItems);
      dndContext.setItemsPerDroppableId(droppableId, newItems);
      setItemsState(newItems);
      setOriginalItems(newItems);
    } else if (!isEqual(prevDndContextItems.current, dndContext.itemsPerDroppableId[droppableId])) {
      newItems = dndContext.itemsPerDroppableId[droppableId] || [];
      prevDndContextItems.current = _toConsumableArray(newItems);
      setItemsState(newItems);
    } else if (!dndContext.itemsPerDroppableId[droppableId]) {
      newItems = items || [];
      dndContext.setItemsPerDroppableId(droppableId, newItems);
    }
  }, [dndContext, droppableId, items]);
  var content = typeof children === "function" ? children({
    isOver: isOver,
    items: itemsState || []
  }) : children;
  content = /*#__PURE__*/jsx("div", {
    ref: setNodeRef,
    children: content
  });
  if (itemsState && direction) {
    var sortingStrategy;
    switch (direction) {
      case "horizontal":
        sortingStrategy = horizontalListSortingStrategy;
        break;
      case "vertical":
        sortingStrategy = verticalListSortingStrategy;
        break;
      case "grid":
        sortingStrategy = rectSortingStrategy;
        break;
    }
    content = /*#__PURE__*/jsx(SortableContext, {
      id: droppableId,
      items: itemsState,
      strategy: sortingStrategy,
      children: content
    });
  }
  var context = React.useMemo(function () {
    return {
      direction: direction,
      mode: mode
    };
  }, [direction, mode]);
  if (mode === "copy") {
    content = /*#__PURE__*/jsxs("div", {
      style: {
        position: "relative"
      },
      children: [typeof children === "function" ? children({
        items: originalItems
      }) : children, /*#__PURE__*/jsx("div", {
        style: {
          opacity: 0,
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0
        },
        children: content
      })]
    });
  }
  return /*#__PURE__*/jsx(Context.Provider, {
    value: context,
    children: content
  });
}
DndDroppableV2.displayName = "DndDroppableV2";
function useDndDroppableContext() {
  return React.useContext(Context);
}

export { DndDroppableV2, useDndDroppableContext };
